/**
 * Reine Hilfen für den Reiter „Auswertung“: der Pfad aus Dimensionen (Breadcrumbs), der Filter eines
 * Knotens, Beschriftungen. Der Baum selbst kommt Ebene für Ebene vom Server
 * (tri-server docs/fm-baselining-entwurf.md 7.1–7.2).
 */

export const DIMENSIONS = [
  "site",
  "category1",
  "category2",
  "category3",
  "supplier",
  "year",
  "month",
  "budgetType",
  "activity",
  "costCenter",
  "presentation",
  "extraction",
] as const;
export type Dimension = (typeof DIMENSIONS)[number];

export const DEFAULT_PATH: Dimension[] = ["site", "category1", "category2", "supplier"];

export const NONE = "__none";

/** Der Pfad aus der Adresse — unbekannte und doppelte Glieder fallen heraus. */
export const pathFrom = (raw: string | null): Dimension[] => {
  if (!raw) return DEFAULT_PATH;
  const seen = new Set<string>();
  const path = raw
    .split(",")
    .map((d) => d.trim())
    .filter((d): d is Dimension => (DIMENSIONS as readonly string[]).includes(d) && !seen.has(d) && (seen.add(d), true));
  return path.length ? path : DEFAULT_PATH;
};

/** Welche Dimensionen ein Glied an Stelle `index` annehmen darf: die eigene und alle, die noch fehlen. */
export const choicesAt = (path: Dimension[], index: number): Dimension[] =>
  DIMENSIONS.filter((d) => d === path[index] || !path.includes(d));

export const replaceAt = (path: Dimension[], index: number, dimension: Dimension): Dimension[] =>
  path.map((d, i) => (i === index ? dimension : d));

export const removeAt = (path: Dimension[], index: number): Dimension[] => (path.length > 1 ? path.filter((_, i) => i !== index) : path);

export const appendNext = (path: Dimension[]): Dimension[] => {
  const next = DIMENSIONS.find((d) => !path.includes(d));
  return next ? [...path, next] : path;
};

/** Der Filter eines Knotens: die Werte seiner Vorfahren, je unter der Dimension ihrer Ebene. */
export const nodeFilter = (path: Dimension[], values: string[]): Record<string, string> =>
  Object.fromEntries(values.map((v, i) => [path[i], v]));

/** Ein stabiler Schlüssel je Knoten, für Aufklappen und Auswahl. */
export const nodeId = (values: string[]): string => JSON.stringify(values);

/** Pure: die Adresse einer erzeugten Datei — der Server liefert einen Speicherpfad, ausgeliefert über `/documents`. */
export const documentHref = (serverUrl: string, url: string): string => (/^https?:\/\//.test(url) ? url : `${serverUrl}/documents${url}`);

export const formatEuro = (amount: number, language = "de"): string =>
  new Intl.NumberFormat(language.startsWith("en") ? "en-GB" : "de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount);

export const formatShare = (share: number): string => `${Math.round(share * 100)} %`;

// ------------------------------------------------------------------ Buckets (docs/fm-baselining-entwurf.md 7.3)

/** Pseudo-Buckets: keine Regel trifft, oder gleich spezifische Regeln widersprechen sich. */
export const REST = "_rest";
export const CONFLICT = "_conflict";

/**
 * Farbe je Farbplatz eines Buckets — die kategoriale Palette (hell/dunkel je eigener Schritt), feste
 * Reihenfolge. Rot fehlt mit Absicht: Es ist der Status „kritisch“ des Konflikts. Als vollständige
 * Klassennamen, damit Tailwind sie findet.
 */
export const BUCKET_SWATCHES = [
  "bg-[#2a78d6] dark:bg-[#3987e5]",
  "bg-[#eb6834] dark:bg-[#d95926]",
  "bg-[#1baf7a] dark:bg-[#199e70]",
  "bg-[#eda100] dark:bg-[#c98500]",
  "bg-[#e87ba4] dark:bg-[#d55181]",
  "bg-[#008300] dark:bg-[#008300]",
  "bg-[#6250d6] dark:bg-[#9085e9]",
] as const;
export const REST_SWATCH = "bg-muted-foreground/30";
export const CONFLICT_SWATCH = "bg-[#d03b3b]";

/** Die Farbklasse eines Schlüssels (Bucket-Id, `_rest`, `_conflict`). */
export const swatchFor = (key: string, slots: Record<string, number | null>): string => {
  if (key === CONFLICT) return CONFLICT_SWATCH;
  const slot = slots[key];
  return typeof slot === "number" && BUCKET_SWATCHES[slot] ? BUCKET_SWATCHES[slot] : REST_SWATCH;
};

/**
 * Die Segmente des Verteilungsbalkens eines Knotens: Buckets in ihrer Reihenfolge, dann Konflikt, dann
 * Rest; leere fallen weg. Anteil am Betrag (Gutschriften zählen mit ihrem Betrag, nicht als Abzug).
 */
export const distributionSegments = (
  byBucket: Record<string, number> | undefined,
  order: string[],
): Array<{ key: string; share: number; amount: number }> => {
  if (!byBucket) return [];
  const keys = [...order.filter((k) => byBucket[k]), ...[CONFLICT, REST].filter((k) => byBucket[k])];
  const total = keys.reduce((sum, k) => sum + Math.abs(byBucket[k]), 0);
  return total ? keys.map((k) => ({ key: k, amount: byBucket[k], share: Math.abs(byBucket[k]) / total })) : [];
};

/** Der Datentyp, unter dem ein Baumknoten gezogen wird. */
export const NODE_DRAG_TYPE = "application/x-baselining-node";

// ------------------------------------------------------------------ Zeitraum (docs/fm-baselining-testbestand.md §9)

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Der Zeitraum aus der Adresse; eine unlesbare oder vertauschte Grenze fällt weg, statt eine Abfrage zu sprengen. */
export const periodFrom = (from: string | null, to: string | null): { from?: string; to?: string } => {
  const f = from && MONTH.test(from) ? from : undefined;
  const t = to && MONTH.test(to) ? to : undefined;
  if (f && t && f > t) return { from: t, to: f };
  return { ...(f ? { from: f } : {}), ...(t ? { to: t } : {}) };
};

const monthOf = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

/** Die Schnellwahl: alles, die Jahre ab 2024 bis heute, die letzten 12 Monate einschließlich des laufenden. */
export const quickPeriods = (today: Date, firstYear = 2024): Array<{ key: string; from?: string; to?: string }> => {
  const years = [];
  for (let y = firstYear; y <= today.getFullYear(); y += 1) years.push({ key: String(y), from: `${y}-01`, to: `${y}-12` });
  const start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
  return [{ key: "all" }, ...years, { key: "last12", from: monthOf(start), to: monthOf(today) }];
};

/** Ob ein Zeitraum genau einer Schnellwahl entspricht — für die hervorgehobene Taste. */
export const samePeriod = (a: { from?: string; to?: string }, b: { from?: string; to?: string }): boolean =>
  (a.from ?? "") === (b.from ?? "") && (a.to ?? "") === (b.to ?? "");
